<template>
  <div>
    <the-breadcrumbs></the-breadcrumbs>
    <page-content page-title="Kontoeinstellungen">
      <el-form
        ref="userForm"
        v-loading="loading"
        :model="user"
        :rules="rules"
        label-position="top"
        style="max-width: 700px;"
        @keyup.enter.native="checkForm"
      >
        <el-form-item label="E-Mail-Adresse" prop="email">
          <el-input v-model.trim="user.email"></el-input>
        </el-form-item>

        <el-form-item
          v-if="$store.getters['auth/isRegionUser']"
          label="Ihr Name"
          prop="name"
        >
          <el-input v-model="user.name"></el-input>
        </el-form-item>

        <el-form-item label="Neues Passwort wählen" prop="password">
          <el-input
            v-model="user.password"
            type="password"
            placeholder="Bitte leer lassen, falls Sie ihr Passwort nicht ändern möchten"
          ></el-input>
        </el-form-item>

        <el-form-item
          label="Neues Passwort wiederholen"
          prop="passwordConfirmation"
        >
          <el-input
            v-model="user.passwordConfirmation"
            type="password"
            placeholder="Bitte leer lassen, falls Sie ihr Passwort nicht ändern möchten"
          ></el-input>
        </el-form-item>

        <el-form-item
          v-if="$store.getters['auth/isRegionUser']"
          label="E-Mails zu Veranstaltungen erhalten"
        >
          <el-switch
            v-model="user.receive_event_emails"
            active-text="Ja"
            inactive-text="Nein"
          ></el-switch>
        </el-form-item>

        <el-form-item
          v-if="
            $store.getters['auth/isRegionAdmin'] ||
              $store.getters['auth/canManageNews']
          "
          label="E-Mails zu relevanten Neuigkeiten der letzten 7 Tage erhalten"
        >
          <el-switch
            v-model="user.receive_relevant_news_emails"
            active-text="Ja"
            inactive-text="Nein"
          ></el-switch>
        </el-form-item>

        <portal to="fixed-footer-left">
          <el-button type="primary" :loading="saving" @click="checkForm">
            Änderungen speichern
          </el-button>
        </portal>
      </el-form>

      <!-- Two-Factor Authentication Section -->
      <el-divider></el-divider>
      <h3>Zwei-Faktor-Authentifizierung (2FA)</h3>

      <div v-if="!loading" class="two-factor-section">
        <!-- 2FA Status Display -->
        <div v-if="user.two_factor_enabled" class="two-factor-status">
          <el-alert
            title="Zwei-Faktor-Authentifizierung ist aktiviert"
            type="success"
            :closable="false"
            show-icon
          ></el-alert>

          <!-- Disable 2FA -->
          <div class="two-factor-actions">
            <el-button type="danger" plain @click="showDisableDialog = true">
              2FA deaktivieren
            </el-button>
            <el-button
              type="warning"
              plain
              @click="showRegenerateCodesDialog = true"
            >
              Backup-Codes neu generieren
            </el-button>
          </div>
        </div>

        <!-- 2FA Not Enabled -->
        <div v-else>
          <el-alert
            title="Zwei-Faktor-Authentifizierung ist nicht aktiviert"
            type="info"
            :closable="false"
            show-icon
          >
            <p>
              Schützen Sie Ihr Konto mit einer zusätzlichen Sicherheitsebene.
              Sie benötigen eine Authenticator-App wie
              <a
                href="https://support.google.com/accounts/answer/1066447?hl=de&co=GENIE.Platform%3DAndroid&oco=0"
                target="_blank"
                rel="noopener"
                >Google Authenticator</a
              >
              oder
              <a href="https://www.authy.com/" target="_blank" rel="noopener"
                >Authy</a
              >.
            </p>
          </el-alert>

          <!-- Setup Flow -->
          <div v-if="!twoFactorSetup.active" class="two-factor-actions">
            <el-button
              type="primary"
              :loading="twoFactorSetup.loading"
              @click="startTwoFactorSetup"
            >
              2FA einrichten
            </el-button>
          </div>

          <!-- Setup in progress -->
          <div v-else class="two-factor-setup">
            <el-steps
              :active="twoFactorSetup.step"
              finish-status="success"
              align-center
              class="two-factor-steps"
            >
              <el-step title="QR-Code"></el-step>
              <el-step title="Verifizieren"></el-step>
              <el-step title="Backup-Codes"></el-step>
            </el-steps>

            <!-- Step 1: QR Code -->
            <div v-if="twoFactorSetup.step === 0" class="setup-step">
              <p>Scannen Sie diesen QR-Code mit Ihrer Authenticator-App:</p>
              <div class="qr-code-container">
                <canvas ref="qrCodeCanvas" class="qr-code"></canvas>
              </div>
              <p class="manual-entry">
                <strong>Manuelle Eingabe:</strong><br />
                <code>{{ twoFactorSetup.secret }}</code>
              </p>
              <div class="setup-actions">
                <el-button type="primary" @click="twoFactorSetup.step = 1">
                  Weiter
                </el-button>
                <el-button
                  :disabled="twoFactorSetup.verifying"
                  @click="cancelTwoFactorSetup"
                >
                  Abbrechen
                </el-button>
              </div>
            </div>

            <!-- Step 2: Verify OTP -->
            <div v-if="twoFactorSetup.step === 1" class="setup-step">
              <p>
                Geben Sie den 6-stelligen Code aus Ihrer Authenticator-App ein:
              </p>
              <el-input
                v-model="twoFactorSetup.otpCode"
                placeholder="000000"
                maxlength="6"
                style="max-width: 200px;"
                @keyup.enter.native="verifyAndEnableTwoFactor"
              ></el-input>
              <div class="setup-actions">
                <el-button
                  type="primary"
                  :loading="twoFactorSetup.verifying"
                  :disabled="twoFactorSetup.otpCode.length !== 6"
                  @click="verifyAndEnableTwoFactor"
                >
                  Verifizieren und aktivieren
                </el-button>
                <el-button
                  :disabled="twoFactorSetup.verifying"
                  @click="goToQrCodeStep"
                >
                  Zurück
                </el-button>
              </div>
            </div>

            <!-- Step 3: Backup Codes -->
            <div v-if="twoFactorSetup.step === 2" class="setup-step">
              <el-alert
                title="Wichtig: Sichern Sie Ihre Backup-Codes!"
                type="warning"
                :closable="false"
                show-icon
              >
                <p>
                  Diese Codes können jeweils einmal verwendet werden, falls Sie
                  keinen Zugriff auf Ihre Authenticator-App haben. Bewahren Sie
                  sie sicher auf!
                </p>
              </el-alert>
              <div class="backup-codes">
                <code
                  v-for="code in twoFactorSetup.backupCodes"
                  :key="code"
                  class="backup-code"
                >
                  {{ code }}
                </code>
              </div>
              <div class="setup-actions">
                <el-button type="primary" @click="copyBackupCodes">
                  Codes kopieren
                </el-button>
                <el-button type="success" @click="finishTwoFactorSetup">
                  Fertig
                </el-button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Disable 2FA Dialog -->
      <el-dialog
        title="Zwei-Faktor-Authentifizierung deaktivieren"
        :visible.sync="showDisableDialog"
        width="400px"
        @close="disablePassword = ''"
      >
        <p>Bitte geben Sie Ihr Passwort ein, um 2FA zu deaktivieren:</p>
        <el-input
          v-model="disablePassword"
          type="password"
          placeholder="Passwort"
          show-password
          @keyup.enter.native="disableTwoFactor"
        ></el-input>
        <span slot="footer" class="dialog-footer">
          <el-button @click="showDisableDialog = false">Abbrechen</el-button>
          <el-button
            type="danger"
            :loading="disabling"
            :disabled="disabling"
            @click="disableTwoFactor"
          >
            Deaktivieren
          </el-button>
        </span>
      </el-dialog>

      <!-- Regenerate Backup Codes Dialog -->
      <el-dialog
        title="Backup-Codes neu generieren"
        :visible.sync="showRegenerateCodesDialog"
        width="500px"
        @close="closeRegenerateDialog"
      >
        <div v-if="!regeneratedBackupCodes.length">
          <el-alert type="warning" :closable="false" show-icon>
            <p>
              Achtung: Alle bisherigen Backup-Codes werden ungültig!
            </p>
          </el-alert>
          <p style="margin-top: 16px;">Bitte geben Sie Ihr Passwort ein:</p>
          <el-input
            v-model="regeneratePassword"
            type="password"
            placeholder="Passwort"
            show-password
            @keyup.enter.native="regenerateBackupCodes"
          ></el-input>
        </div>
        <div v-else>
          <el-alert
            title="Neue Backup-Codes generiert"
            type="success"
            :closable="false"
            show-icon
          >
            <p>Bewahren Sie diese Codes sicher auf!</p>
          </el-alert>
          <div class="backup-codes">
            <code
              v-for="code in regeneratedBackupCodes"
              :key="code"
              class="backup-code"
            >
              {{ code }}
            </code>
          </div>
        </div>
        <span slot="footer" class="dialog-footer">
          <template v-if="!regeneratedBackupCodes.length">
            <el-button @click="showRegenerateCodesDialog = false"
              >Abbrechen</el-button
            >
            <el-button
              type="primary"
              :loading="regenerating"
              :disabled="regenerating"
              @click="regenerateBackupCodes"
            >
              Neu generieren
            </el-button>
          </template>
          <template v-else>
            <el-button type="primary" @click="copyRegeneratedBackupCodes">
              Codes kopieren
            </el-button>
            <el-button type="success" @click="closeRegenerateDialog">
              Fertig
            </el-button>
          </template>
        </span>
      </el-dialog>
    </page-content>
  </div>
</template>

<script>
import TheBreadcrumbs from "@/components/TheBreadcrumbs"
import PageContent from "@/components/PageContent"
import FixedFooterMixin from "@/mixins/fixed_footer"
import FormMixin from "@/mixins/form.js"
import MessagesMixin from "@/mixins/messages"
import ApiErrorHandlerMixin from "@/mixins/api_error_handler.js"
import UserRepository from "@/repositories/user_repository.js"
import TwoFactorRepository from "@/repositories/two_factor_repository.js"
import QRCode from "qrcode"

export default {
  metaInfo: {
    title: "Kontoeinstellungen"
  },
  components: {
    TheBreadcrumbs,
    PageContent
  },
  mixins: [FixedFooterMixin, MessagesMixin, FormMixin, ApiErrorHandlerMixin],
  data() {
    return {
      initialEmail: null,
      loading: true,
      saving: false,
      user: {},
      rules: {
        email: [
          {
            type: "email",
            required: true,
            message: "Bitte gültige E-Mail-Adresse angeben",
            trigger: "blur"
          }
        ],
        name: [
          {
            required: true,
            message: "Bitte Ihren Namen angeben",
            trigger: "blur"
          }
        ],
        password: [
          {
            min: 10,
            message: "Das Passwort muss mind. 10 Zeichen haben",
            trigger: "change"
          }
        ],
        passwordConfirmation: [
          { validator: this.validatePasswords, trigger: "blur" },
          {
            min: 10,
            message: "Das Passwort muss mind. 10 Zeichen haben",
            trigger: "change"
          }
        ]
      },
      // Two-Factor Authentication
      twoFactorSetup: {
        active: false,
        loading: false,
        step: 0,
        provisioningUri: "",
        secret: "",
        otpCode: "",
        verifying: false,
        backupCodes: []
      },
      showDisableDialog: false,
      disablePassword: "",
      disabling: false,
      showRegenerateCodesDialog: false,
      regeneratePassword: "",
      regenerating: false,
      regeneratedBackupCodes: []
    }
  },
  computed: {},
  async created() {
    try {
      this.user = await UserRepository.get()
      this.initialEmail = this.user.email
      this.loading = false
    } catch (error) {
      this.loading = false
      this.handleApiError(error)
    }
  },
  methods: {
    async saveUserSettings() {
      this.saving = true

      try {
        await UserRepository.update(this.user)
        this.saving = false
        if (this.initialEmail === this.user.email) {
          this.showFormUpdateSuccessMessage()
        } else {
          this.$message({
            message: `Die Änderungen wurden erfolgreich gespeichert. Bitte loggen Sie sich mit Ihrer neuen E-Mail-Adresse ein.`,
            type: "success",
            showClose: true
          })
          this.$store.commit("auth/logout")
          this.$router.push({ name: "LoginPage" })
        }
      } catch (error) {
        this.saving = false
        this.handleApiError(error)
      }
    },
    checkForm() {
      this.$refs["userForm"].validate(valid => {
        if (valid) {
          this.submitForm()
        } else {
          this.showFormErrors()
        }
      })
    },
    async submitForm() {
      this.saveUserSettings()
    },
    validatePasswords(rule, value, callback) {
      if (value !== this.user.password) {
        callback(new Error("Die Passwörter stimmen nicht überein"))
      } else {
        callback()
      }
    },

    // Two-Factor Authentication Methods
    async startTwoFactorSetup() {
      this.twoFactorSetup.loading = true
      try {
        const data = await TwoFactorRepository.setup()
        this.twoFactorSetup.provisioningUri = data.provisioning_uri
        this.twoFactorSetup.secret = data.secret
        this.twoFactorSetup.active = true
        this.twoFactorSetup.step = 0
        this.twoFactorSetup.loading = false
        // Generate QR code client-side after canvas is rendered
        this.$nextTick(() => {
          this.generateQrCode()
        })
      } catch (error) {
        this.twoFactorSetup.loading = false
        this.handleApiError(error)
      }
    },

    async generateQrCode() {
      if (!this.twoFactorSetup.provisioningUri || !this.$refs.qrCodeCanvas) {
        return
      }
      try {
        await QRCode.toCanvas(
          this.$refs.qrCodeCanvas,
          this.twoFactorSetup.provisioningUri,
          {
            width: 200,
            margin: 2,
            errorCorrectionLevel: "M"
          }
        )
      } catch (error) {
        this.$message({
          message: "QR-Code konnte nicht generiert werden.",
          type: "error",
          showClose: true
        })
      }
    },

    cancelTwoFactorSetup() {
      // Prevent cancellation while verification is in progress to avoid
      // state desync where server enables 2FA but client resets
      if (this.twoFactorSetup.verifying) {
        return
      }
      this.twoFactorSetup.active = false
      this.twoFactorSetup.step = 0
      this.twoFactorSetup.provisioningUri = ""
      this.twoFactorSetup.secret = ""
      this.twoFactorSetup.otpCode = ""
      this.twoFactorSetup.backupCodes = []
    },

    goToQrCodeStep() {
      // Prevent navigation while verification is in progress
      if (this.twoFactorSetup.verifying) {
        return
      }
      this.twoFactorSetup.step = 0
      // Regenerate QR code after canvas is re-rendered
      this.$nextTick(() => {
        this.generateQrCode()
      })
    },

    async verifyAndEnableTwoFactor() {
      if (this.twoFactorSetup.otpCode.length !== 6) {
        return
      }

      this.twoFactorSetup.verifying = true
      try {
        const data = await TwoFactorRepository.enable(
          this.twoFactorSetup.otpCode
        )
        this.twoFactorSetup.backupCodes = data.backup_codes
        this.twoFactorSetup.step = 2
        this.twoFactorSetup.verifying = false
      } catch (error) {
        this.twoFactorSetup.verifying = false
        if (error.response && error.response.status === 422) {
          this.$message({
            message: "Ungültiger Code. Bitte versuchen Sie es erneut.",
            type: "error",
            showClose: true
          })
        } else {
          this.handleApiError(error)
        }
      }
    },

    copyBackupCodes() {
      const codesText = this.twoFactorSetup.backupCodes.join("\n")
      this.copyToClipboard(codesText)
    },

    copyRegeneratedBackupCodes() {
      const codesText = this.regeneratedBackupCodes.join("\n")
      this.copyToClipboard(codesText)
    },

    copyToClipboard(text) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          this.$message({
            message: "Codes in die Zwischenablage kopiert",
            type: "success",
            showClose: true
          })
        })
        .catch(() => {
          this.$message({
            message: "Kopieren fehlgeschlagen. Bitte manuell kopieren.",
            type: "warning",
            showClose: true
          })
        })
    },

    async finishTwoFactorSetup() {
      this.user.two_factor_enabled = true
      this.cancelTwoFactorSetup()
      this.$message({
        message: "Zwei-Faktor-Authentifizierung wurde erfolgreich aktiviert!",
        type: "success",
        showClose: true
      })
    },

    async disableTwoFactor() {
      if (this.disabling) return

      if (!this.disablePassword) {
        this.$message({
          message: "Bitte geben Sie Ihr Passwort ein.",
          type: "warning",
          showClose: true
        })
        return
      }

      this.disabling = true
      try {
        await TwoFactorRepository.disable(this.disablePassword)
        this.user.two_factor_enabled = false
        this.showDisableDialog = false
        this.disablePassword = ""
        this.disabling = false
        this.$message({
          message: "Zwei-Faktor-Authentifizierung wurde deaktiviert.",
          type: "success",
          showClose: true
        })
      } catch (error) {
        this.disabling = false
        if (error.response && error.response.status === 422) {
          this.$message({
            message: "Ungültiges Passwort.",
            type: "error",
            showClose: true
          })
        } else {
          this.handleApiError(error)
        }
      }
    },

    async regenerateBackupCodes() {
      if (this.regenerating) return

      if (!this.regeneratePassword) {
        this.$message({
          message: "Bitte geben Sie Ihr Passwort ein.",
          type: "warning",
          showClose: true
        })
        return
      }

      this.regenerating = true
      try {
        const data = await TwoFactorRepository.regenerateBackupCodes(
          this.regeneratePassword
        )
        this.regeneratedBackupCodes = data.backup_codes
        this.regenerating = false
      } catch (error) {
        this.regenerating = false
        if (error.response && error.response.status === 422) {
          this.$message({
            message: "Ungültiges Passwort.",
            type: "error",
            showClose: true
          })
        } else {
          this.handleApiError(error)
        }
      }
    },

    closeRegenerateDialog() {
      this.showRegenerateCodesDialog = false
      this.regeneratePassword = ""
      this.regeneratedBackupCodes = []
    }
  }
}
</script>

<style lang="scss" scoped>
.two-factor-section {
  max-width: 700px;
}

.two-factor-status {
  margin-top: 16px;
}

.two-factor-actions {
  margin-top: 16px;
  display: flex;
  gap: 12px;
}

.two-factor-setup {
  margin-top: 24px;
}

.two-factor-steps {
  margin-bottom: 8px;

  ::v-deep .el-step__title {
    font-size: 13px;
    white-space: nowrap;
  }
}

.setup-step {
  margin-top: 24px;
  padding: 20px;
  background: #f5f7fa;
  border-radius: 8px;
}

.qr-code-container {
  display: flex;
  justify-content: center;
  margin: 20px 0;
}

.qr-code {
  border: 4px solid white;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.manual-entry {
  text-align: center;
  margin: 16px 0;
  padding: 12px;
  background: white;
  border-radius: 4px;

  code {
    font-size: 14px;
    letter-spacing: 1px;
    word-break: break-all;
  }
}

.setup-actions {
  margin-top: 16px;
  display: flex;
  gap: 12px;
}

.backup-codes {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  margin: 16px 0;
  padding: 16px;
  background: #f5f7fa;
  border-radius: 8px;
}

.backup-code {
  font-family: monospace;
  font-size: 14px;
  padding: 8px 12px;
  background: white;
  border-radius: 4px;
  text-align: center;
}

.two-factor-section a {
  color: #409eff;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}
</style>
